import { useEffect, useState } from 'react';
import { useAsyncValue, useDateService } from 'framework/utils';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { ClientPortalTaskComment, ClientPortalTaskCommentReply } from '../../types';
import { CommentActions, CommentActiveForm } from './types';
import useClientPortalService from '../../useClientPortalService';

type UseCommentsSectionStateProps = {
  initialComments: ClientPortalTaskComment[];
  projectId: string;
  taskId: string;
  refetchComments: () => Promise<unknown>;
};

export const useCommentsSectionState = ({
  initialComments,
  projectId,
  taskId,
  refetchComments,
}: UseCommentsSectionStateProps) => {
  const { datesDifference, getToday } = useDateService();
  const { getUserAlias } = useAuthenticatedUserService();
  const { createClientPortalTaskComment, createClientPortalTaskCommentReply, updateClientPortalTaskComment } =
    useClientPortalService();

  const currentUserAlias = useAsyncValue(() => getUserAlias()) || '';
  const [comments, setComments] = useState<ClientPortalTaskComment[]>(initialComments);
  const [activeForm, setActiveForm] = useState<CommentActiveForm>(null);

  useEffect(() => {
    setComments(initialComments);
    setActiveForm(null);
  }, [initialComments, taskId]);

  const cancelActiveForm = () => setActiveForm(null);

  const updateActiveFormText = (text: string) => {
    setActiveForm(currentForm => (currentForm ? { ...currentForm, text } : currentForm));
  };

  const createComment = async () => {
    const text = activeForm?.text.trim();
    if (activeForm?.type !== 'create' || !text) return;

    await createClientPortalTaskComment(projectId, taskId, { text });
    await refetchComments();
    cancelActiveForm();
  };

  const createReply = async () => {
    const text = activeForm?.text.trim();
    if (activeForm?.type !== 'reply' || !text) return;

    await createClientPortalTaskCommentReply(projectId, taskId, activeForm.commentId, { text });
    await refetchComments();
    cancelActiveForm();
  };

  const updateCommentOrReply = async () => {
    const text = activeForm?.text.trim();
    if (!text) return;

    if (activeForm?.type === 'edit-comment') {
      await updateClientPortalTaskComment(projectId, activeForm.commentId, { text });
    }

    if (activeForm?.type === 'edit-reply') {
      await updateClientPortalTaskComment(projectId, activeForm.replyId, { text });
    }

    await refetchComments();
    cancelActiveForm();
  };

  const submitActiveForm = () => {
    if (activeForm?.type === 'create') {
      createComment();
      return;
    }

    if (activeForm?.type === 'reply') {
      createReply();
      return;
    }

    updateCommentOrReply();
  };

  const actions: CommentActions = {
    currentUserAlias,
    activeForm,
    getRelativeDate: (date: string) => datesDifference(getToday(), date),
    openReplyForm: (commentId: string) => setActiveForm({ type: 'reply', commentId, text: '' }),
    openEditCommentForm: (comment: ClientPortalTaskComment) =>
      setActiveForm({ type: 'edit-comment', commentId: comment.id, text: comment.text }),
    openEditReplyForm: (reply: ClientPortalTaskCommentReply) =>
      setActiveForm({ type: 'edit-reply', replyId: reply.id, text: reply.text }),
    updateActiveFormText,
    submitActiveForm,
    cancelActiveForm,
  };

  return {
    comments,
    isAddingComment: activeForm?.type === 'create',
    openCreateForm: () => setActiveForm({ type: 'create', text: '' }),
    actions,
  };
};
